import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { ErrorStateMatcher, MatSort, MatPaginator } from '@angular/material';
import { ObservableMedia, MediaChange } from '@angular/flex-layout';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { first } from 'rxjs/operators/first';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { combineLatest } from 'rxjs/observable/combineLatest';

import { DataTableSource } from '../../classes/DataTableSource/datatablesource';

import {
    ApplicationService,
    CollegeStaff,
    CollegeInfo,
    CollegeStaffAccessLevel,
} from '../../services/application/application.service';
import { AuthService } from '../../services/authentication/auth.service';
import { DialogService } from '../../services/dialogs/dialogs.service';
import { DownloadService, DownloadFormat, HttpMethod } from '../../services/download/download.service';
import { ErrorService } from '../../services/error/error.service';
import { SplashService } from '../../services/splash/splash.service';
import { UserInfo, UserService } from '../../services/user/user.service';

import { TitleCasePipe } from '../../pipes/titlecase.pipe';

import { validateEmail } from '../../validators/email.validator';
import { BaseOptionBoxDialogOptions, BaseOptionBoxOption } from '../../services/dialogs/optionbox/optionbox.dialogcomponent';

@Component({
    selector: 'app-manage-staff',
    styleUrls: ['managestaff.component.css'],
    templateUrl: 'managestaff.component.html',
})
export class ManageStaffComponent implements OnInit, OnDestroy {
    @ViewChild('paginator') paginator: MatPaginator;
    @ViewChild(MatSort) sorter: MatSort;

    private _subrelease: Subject<void> = new Subject<void>();

    AdminCollege: CollegeInfo;
    AdminCollegeID: number;
    AllCollegeYears: string[];
    AllHeaders: string[] = ['FirstName', 'LastName', 'AccessLevel', 'StaffActions'];
    Colleges: CollegeInfo[];
    NewUserForm: FormGroup;
    NoData: boolean;
    parentErrorStateMatcher: ParentErrorStateMatcher = new ParentErrorStateMatcher();
    PartialViewReady: boolean;
    ShowingNewUserForm: boolean;
    SmallDisplay: boolean;
    StaffData: DataTableSource<CollegeStaff>;
    StaffList: CollegeStaff[];
    User: UserInfo;
    VisibleHeaders: string[];
    VisibleColumns: string[];

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private dialogService: DialogService,
        private downloadService: DownloadService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private mediaChange: ObservableMedia,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnInit() {
        const self = this;

        self.AdminCollege = null;
        self.StaffData = new DataTableSource<CollegeStaff>([], self.paginator, self.sorter, self.SortStaff.bind(self));
        self.StaffList = null;
        self.Colleges = null;
        self.NewUserForm = null;
        self.NoData = false;
        self.PartialViewReady = false;
        self.ShowingNewUserForm = false;
        self.SmallDisplay = false;
        self.User = null;

        self.SmallDisplay = self.mediaChange.isActive('xs') || self.mediaChange.isActive('sm');

        self.mediaChange
            .asObservable()
            .pipe(takeUntil(self._subrelease))
            .subscribe(media => {
                self.SmallDisplay = media.mqAlias === 'xs' || media.mqAlias === 'sm';
                self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
                self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
            });

        // Make an empty new user form
        self.NewUserForm = self.fb.group(
            {
                FirstName: [null, Validators.required],
                LastName: [null, Validators.required],
                Email: [null, [Validators.required, validateEmail]],
                EmailVerify: [null, [Validators.required, validateEmail]],
            },
            { validator: self.EmailVerifyValidator }
        );

        // Get the first non-null UserInfo object so we can begin setting up the component
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(user => user && !!user.AuthToken)
        ).subscribe(user => {
            self.User = user;

            // Set up constant monitoring of the user object, to catch AuthToken updates.
            self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(_user => {
                self.User = _user;
            });

            // Load static data for use in the compoment.
            combineLatest(
                self.appService.GetAllColleges(self.User.AuthToken),
                self.appService.GetAllCollegeYears(),
                self.appService.SelectedAdminCollegeIDSubject,
                function(colleges, collegeyears, adminCollegeID) {
                    return {
                        AdminCollegeID: adminCollegeID,
                        Colleges: colleges,
                        CollegeYears: collegeyears,
                    };
                }
            )
                .pipe(takeUntil(self._subrelease))
                .subscribe(
                    packet => {
                        self.Colleges = packet.Colleges;
                        self.AllCollegeYears = packet.CollegeYears;
                        self.AdminCollegeID = packet.AdminCollegeID;
                        self.AdminCollege = null;

                        if (self.AdminCollegeID != null && self.Colleges) {
                            self.AdminCollege = self.Colleges.find(item => {
                                return item.CollegeID === self.AdminCollegeID;
                            });
                        }

                        // Load the staff for this college, if one has been selected.
                        if (self.AdminCollege) {
                            self.appService.GetAllCollegeStaffForCollege(self.User.AuthToken, self.AdminCollege.CollegeID).subscribe(
                                staff => {
                                    self.UpdateStaff(staff);

                                    // Ready for component use.
                                    self.PartialViewReady = true;
                                },
                                error => {
                                    self.errorService.HandleServiceError(error);
                                }
                            );
                        } else {
                            // No college selected. Release component for viewing.
                            self.PartialViewReady = true;
                        }
                    },
                    error => {
                        self.errorService.HandleServiceError(error);
                    }
                );
        });
    }

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    // #region Add New User Method

    AddNewUser(collegerole: number) {
        const self = this;

        if (collegerole == null) {
            return;
        }

        const form = <NewUserInfo>self.NewUserForm.value;
        const levelName = collegerole === 2 ? 'Administrator' : collegerole === 1 ? 'Advisor' : 'Support Staff';

        // Get the user confirmation before proceeding.
        const message =
            `You are about to create a new user account as a college staff member for the following ` +
            `individual and college:<br /><br /><strong>First Name: </strong>${self.titleCasePipe.transform(form.FirstName)}<br />` +
            `<strong>Last Name: </strong>${self.titleCasePipe.transform(form.LastName)}<br />` +
            `<strong>Email Address: </strong>${form.Email}<br /><strong>College: </strong>${self.AdminCollege.CollegeName}<br />` +
            `<strong>Role: </strong>${levelName}<br /><br />Once the new user account is created, you will be given the ` +
            `username and randomly generated password for the new account, and the user will be sent an introduction email ` +
            `with directions on how to change their password and access the DELTA System. Are you sure you wish to add the ` +
            `new user account as summarized above?`;

        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: `Yes - Add New ${levelName} Account`,
                CancelText: 'No - CANCEL',
            })
            .subscribe(success => {
                if (success === true) {
                    // Add new user account
                    self.splashService
                        .DisplaySplash({
                            TitleText: 'Please Wait',
                            MessageText: 'Creating user account...',
                        })
                        .subscribe(() => {
                            self.authService
                                .AddNewUser(
                                    self.User.AuthToken,
                                    self.User.CurrentRole,
                                    form.FirstName,
                                    form.LastName,
                                    form.Email,
                                    self.AdminCollege.CollegeID,
                                    collegerole
                                )
                                .subscribe(
                                    user => {
                                        self.splashService.CloseSplash();

                                        // Was the result returned a new account? If not, then the email address matched an existing one.
                                        // Show a dialog in that case to the system user.
                                        if (!user.NewAccount) {
                                            // Show the dialog
                                            self.dialogService.OpenMessageBoxDialog({
                                                TitleText: 'Duplicate Email Address Detected',
                                                MessageText:
                                                    `The email address provided (${form.Email}) is already attached to an ` +
                                                    `existing account. The existing account information is provided below:<br /><br />` +
                                                    `<strong>Username: </strong>${user.Username}<br /><strong>User ID#: </strong>` +
                                                    `${user.UserID}<br /><strong>First Name: </strong>${user.FirstName}<br />` +
                                                    `<strong>Last Name: </strong>${user.LastName}<br />` +
                                                    `<strong>Email: </strong>${user.Email}<br /><br />You cannot create a new ` +
                                                    `account using this email address. If the information above looks ` +
                                                    `appropriate for the desired user, you may provide them with the username ` +
                                                    `shown above.`,
                                                OKText: 'OK',
                                            });

                                            const input = <HTMLInputElement>document.querySelector('.firstname');
                                            input.focus();

                                            return;
                                        } else {
                                            // Reset and close the form
                                            self.NewUserForm.reset();
                                            self.ShowingNewUserForm = false;

                                            // Update the listed staff with the returned packet (if available)
                                            self.UpdateStaff(user.Staff);

                                            // Display the created account information
                                            self.splashService.SnackbarSuccess('New User Created Successfully.');

                                            const msg2 =
                                                `The following user account was created:<br /><br />
                                        <strong>Username: </strong>${user.Username}<br />
                                        <strong>Password: </strong>${user.Password}<br />
                                        <strong>Email: </strong>${user.Email}<br />
                                        <strong>Polk User ID#: </strong>${user.UserID}<br />
                                        <strong>College: </strong>${user.CollegeName}<br />
                                        <strong>Role: </strong>${user.RoleName}<br /><br />This user has had an email sent ` +
                                                `to the provided email address above with instructions on how to reset their default ` +
                                                `password and access the DELTA System.`;

                                            self.dialogService
                                                .OpenMessageBoxDialog({
                                                    TitleText: 'New User Account Information',
                                                    MessageText: msg2,
                                                    OKText: 'OK',
                                                })
                                                .subscribe();
                                        }
                                    },
                                    error => {
                                        self.errorService.HandleServiceError(error);
                                    }
                                );
                        });
                }
            });
    }

    // #endregion

    // #region Change Access Method

    ChangeAccess(user: CollegeStaff) {
        const self = this;

        if (!user) {
            return;
        }

        const levelName = user.AccessLevel === 2 ? 'Administrator' : user.AccessLevel === 1 ? 'Advisor' : 'Support staff';

        const message =
            `You have chosen to change the college staff member access for the following user:<br /><br />` +
            `<strong>First Name: </strong>${self.titleCasePipe.transform(user.FirstName)}<br />` +
            `<strong>Last Name: </strong>${self.titleCasePipe.transform(user.LastName)}<br />` +
            `<strong>College: </strong>${self.AdminCollege.CollegeName}<br />` +
            `<strong>Current Level: </strong>${levelName}<br /><br />` +
            `Select a new level by clicking on the appropriate button below. If you wish to cancel this request,` +
            ` click the "Cancel" button at this time.`;

        const adminOption = {
            DisplayText: 'Change to Administrator',
            Response: 2,
            ClosesDialog: true,
            Color: 'warn',
        } as BaseOptionBoxOption;
        const advisorOption = {
            DisplayText: 'Change to Advisor',
            Response: 1,
            ClosesDialog: true,
            Color: 'accent',
        } as BaseOptionBoxOption;
        const supportOption = {
            DisplayText: 'Change to Support',
            Response: 0,
            ClosesDialog: true,
            Color: 'primary',
        } as BaseOptionBoxOption;

        const options = [] as BaseOptionBoxOption[];

        if (user.AccessLevel === 2) {
            // Push in options for advisor and support
            options.push(advisorOption, supportOption);
        } else if (user.AccessLevel === 1) {
            // Push in options for administrator and support
            options.push(adminOption, supportOption);
        } else {
            // Push in options for administrator and advisor
            options.push(adminOption, advisorOption);
        }

        // Add a cancel button
        options.unshift({ Response: null, DisplayText: 'Cancel', ClosesDialog: true, Color: 'warn' });

        // Get a confirmation and the desired change option for this employee.
        self.dialogService
            .OpenOptionBoxDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: 'Yes - Remove',
                CancelText: 'No - CANCEL',
                Options: options,
            })
            .subscribe(result => {
                if (result != null) {
                    // User has opted to proceed. Signal the system to change the access level for this person based upon what
                    // was just selected.
                    self.appService
                        .UpdateCollegeStaffAccessLevel(self.User.AuthToken, self.User.CurrentRole, user.StaffID, result)
                        .subscribe(
                            staff => {
                                // Completed. Refresh the list of staff from what was just returned.
                                self.UpdateStaff(staff);

                                // Toast the success
                                const newLevelName = result === 2 ? 'administrator' : result === 1 ? 'advisor' : 'support staff';

                                self.splashService.SnackbarSuccess(`User access changed to ${newLevelName} successfully.`);
                            },
                            error => {
                                self.errorService.HandleServiceError(error);
                            }
                        );
                }
            });
    }

    // #endregion

    // #region Download Staff PDF Method

    DownloadStaffPDF() {
        const self = this;

        // Set splash
        self.splashService
            .DisplaySplash({
                TitleText: 'Please wait',
                MessageText: 'Downloading College Staff PDF...',
            })
            .pipe(takeUntil(self._subrelease))
            .subscribe(() => {
                self.downloadService
                    .RetrieveDownload(
                        HttpMethod.GET,
                        `Api/Services/PDF/PDFDownloadStaffList`,
                        {
                            AuthToken: self.User.AuthToken,
                            CollegeID: self.AdminCollege.CollegeID,
                            Attachment: true,
                        },
                        DownloadFormat.PDF
                    )
                    .pipe(takeUntil(self._subrelease))
                    .subscribe(
                        success => {
                            // Drop the splash?
                            self.splashService.CloseSplash();
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
            });
    }

    // #endregion

    // #region Email Verify Validate Method

    EmailVerifyValidator(grp: FormGroup) {
        const gval = grp.value;

        if (gval.Email !== gval.EmailVerify) {
            return {
                EmailVerifyValidate: 'mismatch',
            };
        }

        return null;
    }

    // #endregion

    // #region Remove Access Method

    RemoveAccess(user: CollegeStaff) {
        const self = this;

        if (!user) {
            return;
        }

        const levelName = user.AccessLevel === 2 ? 'administrator' : user.AccessLevel === 1 ? 'advisor' : 'support staff';

        const message =
            `You have chosen to remove the following user as a(n) ${levelName} for ${self.AdminCollege.CollegeName}<br /><br />
        <strong>First Name: </strong>${self.titleCasePipe.transform(user.FirstName)}<br />
        <strong>Last Name: </strong>${self.titleCasePipe.transform(user.LastName)}<br /><br />
        Once removed, this user will no longer have ${levelName} access to this college. If this person is no longer ` +
            `a college staff member for any other college, they will no longer have college access to the DELTA system after this ` +
            `process completes. Are you sure you wish to continue?`;

        // Get a confirmation
        self.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Confirmation Requested',
                MessageText: message,
                OKText: 'Yes - Remove',
                CancelText: 'No - CANCEL',
            })
            .subscribe(result => {
                if (result != null) {
                    // User has opted to proceed. Signal the system to remove this person entirely from the system.
                    self.appService.RemoveCollegeStaffFromCollege(self.User.AuthToken, user.StaffID).subscribe(
                        staff => {
                            // Completed. Refresh the list of staff from what was just returned.
                            self.UpdateStaff(staff);

                            // Toast the success
                            self.splashService.SnackbarSuccess(`User was removed as a(n) ${levelName} successfully.`);
                        },
                        error => {
                            self.errorService.HandleServiceError(error);
                        }
                    );
                }
            });
    }

    // #endregion

    // #region Reset New User Form Method

    ResetNewUserForm() {
        this.NewUserForm.reset();
        this.ShowingNewUserForm = false;
    }

    // #endregion

    // #region Show New User Form Method

    ShowNewUserForm() {
        this.ShowingNewUserForm = !this.ShowingNewUserForm;
    }

    // #endregion

    // #region Show User Search Method

    ShowUserSearch() {
        const self = this;

        // Pop up a user search dialog.
        self.dialogService
            .OpenUserSearchDialog({
                TitleText: 'DELTA User Search',
                MessageText: `Use the following options to search for a DELTA user:`,
                CanAddAsAdministrator: true,
            })
            .subscribe(chosenUser => {
                if (chosenUser != null) {
                    // Get a confirmation on adding this user as an advisor/administrator.
                    const levelName =
                        chosenUser.AccessLevel === 2 ? 'administrator' : chosenUser.AccessLevel === 1 ? 'advisor' : 'support staff';

                    self.dialogService
                        .OpenConfirmationDialog({
                            TitleText: 'Confirmation Requested',
                            MessageText:
                                `You are about to add the following user as an <strong>${levelName}</strong> ` +
                                `for this college:</div><br />` +
                                `<div><strong>First name: </strong>${self.titleCasePipe.transform(chosenUser.User.FirstName)}</div>` +
                                `<div><strong>Last name: </strong>${self.titleCasePipe.transform(chosenUser.User.LastName)}</div>` +
                                `<div><strong>Username: </strong>${chosenUser.User.Username}</div>` +
                                `<div><strong>City: </strong>${chosenUser.User.City}</div>` +
                                `<div><strong>Email: </strong>${chosenUser.User.Email}</div><br />` +
                                `<div>Are you sure? Click the "Add Now" button to add this user as an ${levelName}, or click ` +
                                `the "Cancel" button to return to the search results.`,
                            OKText: 'Add Now',
                            CancelText: 'Cancel',
                        })
                        .subscribe(result => {
                            if (result === true) {
                                // User opted to proceed.
                                self.appService
                                    .AddCollegeStaffToCollege(
                                        self.User.AuthToken,
                                        chosenUser.User.UserID,
                                        self.AdminCollege.CollegeID,
                                        chosenUser.AccessLevel
                                    )
                                    .subscribe(
                                        staff => {
                                            // Update the list of known staff
                                            self.UpdateStaff(staff);

                                            // Toast the success
                                            self.splashService.SnackbarSuccess(`User added as ${levelName} successfully.`);
                                        },
                                        error => {
                                            self.errorService.HandleServiceError(error);
                                        }
                                    );
                            }
                        });
                }
            });
    }

    // #endregion

    // #region Sort Staff Method

    SortStaff(First: CollegeStaff, Second: CollegeStaff) {
        const self = this;

        let PropA: string | number;
        let PropB: string | number;

        switch (self.sorter.active) {
            case 'LastName':
                [PropA, PropB] = [First.LastName, Second.LastName];
                break;
            case 'FirstName':
                [PropA, PropB] = [First.FirstName, Second.FirstName];
                break;
            case 'AccessLevel':
                [PropA, PropB] = [First.AccessLevel, Second.AccessLevel];
                break;
        }

        const ValueA = PropA != null && PropA !== '' ? (isNaN(+PropA) ? PropA : +PropA) : PropA;
        const ValueB = PropB != null && PropB !== '' ? (isNaN(+PropB) ? PropB : +PropB) : PropB;

        return (ValueA < ValueB ? -1 : 1) * (self.sorter.direction === 'asc' ? 1 : -1);
    }

    // #endregion

    // #region Update Staff Method

    UpdateStaff(staff: CollegeStaff[]) {
        const self = this;

        self.StaffList = staff || [];

        if (!self.StaffList || !self.StaffList.length) {
            self.NoData = true;

            // Put in a fake record to trip the display message
            self.StaffData.Update([{}]);
        } else {
            self.NoData = false;
            self.StaffData.Update(self.StaffList);
        }

        self.VisibleHeaders = self.SmallDisplay ? ['SmallDisplay'] : self.AllHeaders.slice();
        self.VisibleColumns = self.NoData ? ['NoData'] : self.VisibleHeaders.slice();
    }

    // #endregion
}

// #region Custom Classes

// #region New User Info Class

export class NewUserInfo {
    FirstName?: string;
    LastName?: string;
    Email?: string;
    EmailVerify?: string;
}

// #endregion

// #region Parent Error State Matcher Class

export class ParentErrorStateMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        const isSubmitted = !!(form && form.submitted);
        const controlTouched = !!(control && (control.dirty || control.touched));
        const controlInvalid = !!(control && control.invalid);
        const parentInvalid = !!(
            control &&
            control.parent &&
            control.parent.invalid &&
            (control.parent.dirty || control.parent.touched)
        );

        return isSubmitted || (controlTouched && (controlInvalid || parentInvalid));
    }
}

// #endregion

// #endregion



// WEBPACK FOOTER //
// ./src/app/managestaff/managestaff.component.ts